import _ from 'lodash'
import type {DocumentManager, CreateExtArgs} from '@wix/document-manager-core'
import type {CompRef, Pointer} from '@wix/document-services-types'
import type {
    HooksExtensionApi,
    ManifestExtensionAPI,
    SchemaExtensionAPI,
    SlotsExtensionAPI,
    StructureExtensionAPI
} from '@wix/document-manager-extensions'
import type {PageMigrator} from '../dataMigrationRunner'
import type {RMApi} from '../../../rendererModel'
import * as constants from '../../../../constants/constants'
import {runOnPageComponents} from './utils/migrationUtil'
import {slotAlreadyExists} from '../../../builderComponents/hooks/autoPopulateAdd'
import {replaceExistingStaticSlotWithDynamic} from '../../../builderComponents/hooks/slotPopulationUtils'
import {VIEW_MODES} from '../../../accessibility/types'
import {DATA_MODEL_HOOKS} from '../../../../hooks'
import {
    transformBuilderDataToFlat,
    transformBuilderDataToNestedObjects
} from '../../../builderComponents/builderComponentsHooks'

const migrateComponent = (documentManager: DocumentManager, pageId: string, compPointer: Pointer) => {
    const {extensionAPI, config, pointers, dal, EVENTS, resourceLoader, eventEmitter} = documentManager
    const {schemaAPI} = extensionAPI as SchemaExtensionAPI
    const {components} = extensionAPI as StructureExtensionAPI
    const {manifest} = extensionAPI as ManifestExtensionAPI
    const {slots} = extensionAPI as SlotsExtensionAPI

    const componentType = components.getComponentType(compPointer)
    const isBuilder = schemaAPI.isSchemaCollectionRegistered(componentType)

    if (!isBuilder) {
        return
    }
    if (componentType === constants.SYSTEM_CONTAINER_TYPE) {
        return
    }

    const createExtArgs = {
        coreConfig: config,
        dal,
        pointers,
        extensionAPI,
        EVENTS,
        resourceLoader,
        eventEmitter
    } as CreateExtArgs

    const existingRootSlots = slots.getPopulatedSlotNames(compPointer as CompRef)
    const containersListIncludeInnerElement = manifest.getData(compPointer, true, [{prop: 'container'}])

    _.forEach(containersListIncludeInnerElement, container => {
        if (slotAlreadyExists(createExtArgs, compPointer, container.prop, container.path, existingRootSlots)) {
            replaceExistingStaticSlotWithDynamic(
                createExtArgs,
                compPointer,
                container.path,
                container.prop,
                container.nestedDataPath
            )
        }
    })
}

const migratePage = (documentManager: DocumentManager, pageId: string) => {
    const {experimentInstance, extensionAPI} = documentManager
    const {rendererModel} = extensionAPI as RMApi
    const {hooks} = extensionAPI as HooksExtensionApi
    if (!experimentInstance.isOpen('dm_refactorStaticSlotToDynamic') || !rendererModel.isBuilderComponentModel()) {
        return
    }

    hooks.registerHookWithExtensionArgs(
        DATA_MODEL_HOOKS.DESERIALIZE.DATA_ITEM_NEST_BEFORE.id,
        transformBuilderDataToNestedObjects
    )

    hooks.registerHookWithExtensionArgs(DATA_MODEL_HOOKS.SERIALIZE.DATA_ITEM_AFTER.id, transformBuilderDataToFlat)

    runOnPageComponents(documentManager, pageId, VIEW_MODES.DESKTOP, migrateComponent)
}

const name = 'migrateStaticSlotsToDynamic'
const version = 1
const experimentalVersions = [{experiment: 'dm_refactorStaticSlotToDynamic', version: 2}]

export const migrateStaticSlotsToDynamic: PageMigrator = {
    migratePage,
    name,
    version,
    experimentalVersions
}
